import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useInswitch } from "../../hooks/useInswitch";
import SelectAdvanced from "../SelectAdvanced/SelectAdvanced";
import InputAdvanced from "../Input/InputAdvanced";
import defaultImg from "../../assets/common/default.svg";
import "./Inswitch.css";
import Spinner from "../Spinner/Spinner";

interface Vendor {
    paymentMethodTypeClass: string;
    paymentMethodTypeDescription: string;
    paymentMethodType: string;
    imageURL: string;
    fields: { [key: string]: any }[];
    direction: string;
    country: string;
}

interface UseInswitchResponse {
    data: Vendor[] | undefined;
}

interface InswitchProps {
    action: string;
    currency: string;
    defaultFields: { [key: string]: any }[];
    onFormDataChange: (data: object) => void;
}

const Inswitch = ({ action, currency, defaultFields, onFormDataChange }: InswitchProps) => {
    const { t } = useTranslation('common');
    const { data: inswitchData }: UseInswitchResponse = useInswitch(action, currency);
    const [selectedMethod, setSelectedMethod] = useState("");
    const [selectedOption, setSelectedOption] = useState("");
    const [formData, setFormData] = useState<{ [key: string]: any }>({});
    const [optionFields, setOptionFields] = useState<Vendor | null>(null);

    useEffect(() => {
        if (inswitchData && selectedOption) {
            const selectedVendor = inswitchData.find(vendor => vendor.paymentMethodType === selectedOption);
            setOptionFields(selectedVendor || null);
        } else {
            setOptionFields(null);
        }
    }, [selectedOption, inswitchData]);

    useEffect(() => {
        if (optionFields) {
            const newFormData = {
                direction: optionFields.direction,
                method: optionFields.paymentMethodType,
                method_type: optionFields.paymentMethodTypeClass,
                data: optionFields.fields?.reduce((acc: { [key: string]: any }, curr: { [key: string]: any }) => {
                    if (curr.mandatory) {
                        acc[curr.name] = "";
                    }
                    return acc;
                }, {}),
                blockchain: optionFields.paymentMethodTypeClass,
                country: optionFields.country
            };
            setFormData(newFormData);
        }
    }, [optionFields]);

    const methodTypes = inswitchData ? Object.fromEntries(
        Array.from(new Set(inswitchData.map(vendor => vendor.paymentMethodTypeClass)))
            .map(methodClass => [
                methodClass,
                {
                    id: methodClass,
                    name: methodClass,
                }
            ])
        ) : {};

    const methodsOptions = inswitchData && selectedMethod ? Object.fromEntries(
        inswitchData
            .filter(method => method.paymentMethodTypeClass === selectedMethod)
            .map(method => [
                method.paymentMethodType,
                {
                    id: method.paymentMethodType,
                    name: method.paymentMethodTypeDescription,
                    logo: method.imageURL || defaultImg,
                }
            ])
        ) : {};

    const handleMethodChange = (value: string) => {
        setSelectedMethod(value);
        setSelectedOption("");
        onFormDataChange({ blockchain: value });
    };

    const handleOptionChange = (value: string) => {
        setSelectedOption(value);
    };

    const handleInputChange = (field: { [key: string]: any }, value: string) => {
        if (field.handleInputChange) {
            field.handleInputChange(value);
        }
        const regex = new RegExp(field.regex);
        let message = "";
        let new_value = "";
        if (value != "" && !regex.test(value)) {
            message = t("invalid_value") || "Invalid value";
            new_value = ""
        }
        else {
            message = ""
            new_value = value
        }
        setFormData((prevState) => ({
            ...prevState,
            data: {
                ...prevState.data,
                [field.name]: new_value,
            },
        }));
        return message;
    };

    useEffect(() => {
        onFormDataChange(formData);
    }, [formData]);

    const formatSelectInputs = (options: string[]) => {
        return Object.fromEntries(
            options.map(option => [
                option,
                { id: option, name: t(option) || option }
            ])
        );
    };

    // // Get the display names for the selected method and option, this was a dropdown but it did not make sense to have a dropdown on a item array
    // const selectedMethodDisplay = selectedMethod ? methodTypes[selectedMethod]?.name : "";
    // const selectedOptionDisplay = selectedOption 
    //     ? methodsOptions[selectedOption]?.name 
    //     : "";

    return (
        <>
            {inswitchData ? (
                <div className="inswitch-fields-cont">
                    <form className="inswitch-fields">
                        {defaultFields.map((field: any, index: number) => (
                            field.mandatory && (
                                <div key={index} className="field-container">
                                    {field.fieldType === 'select' ? (
                                        <SelectAdvanced
                                            value={formData?.data?.[field.name]}
                                            label={t(field.name) || field.name}
                                            items={formatSelectInputs(field.validOptions)}
                                            image={false}
                                            onChange={(e) => {
                                                handleInputChange(field, e);
                                                
                                            }}
                                            isSearchable={true}
                                        />
                                    ) : field ? (
                                        <InputAdvanced field={field} onInputChange={handleInputChange} />
                                    ) : null}
                                </div>
                            )
                        ))}
                        
                        {/* {selectedMethod && selectedOption && (
                            <h1 className="selected-method-display">
                                {selectedMethodDisplay} - {selectedOptionDisplay}
                            </h1>
                        )}  */}
                        
                        {!selectedMethod && (
                            <SelectAdvanced
                                label={t("select_method")}
                                value={selectedMethod}
                                items={methodTypes}
                                image={false}
                                onChange={handleMethodChange}
                                
                            />
                        )}
                        
                        {selectedMethod && !selectedOption && (
                            <SelectAdvanced
                                label={t("select_option")}
                                value={selectedOption}
                                items={methodsOptions}
                                image={true}
                                onChange={handleOptionChange}
                            />
                        )}


                        {optionFields?.fields?.map((field: any, index: number) => (
                            field.mandatory && (
                                <div key={index} className="field-container">
                                    {field.fieldType === 'select' ? (
                                        <SelectAdvanced
                                            value={formData?.data?.[field.name]}
                                            label={t(field.name) || field.name}
                                            items={formatSelectInputs(field.validOptions)}
                                            image={false}
                                            onChange={(e) => {
                                                handleInputChange(field, e);
                                            }}
                                            isSearchable={true}
                                            placeholder={t("type_search")}
                                        />
                                    ) : field ? (
                                        <InputAdvanced field={field} onInputChange={handleInputChange} />
                                    ) : null}
                                </div>
                            )
                        ))}
                        
                        {(selectedMethod === 'bank' || selectedMethod === 'card' || selectedMethod === 'bank_transfer') && 
                            <div className="inswitch-disclaimer">*{t("inswitch_disclaimer")}</div>
                        }
                    </form>
                </div>
            ) : (
                <Spinner />
            )}
        </>
    );
};

export default Inswitch;